import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, Search, ArrowUpDown, SlidersHorizontal } from "lucide-react";
import ProductCard from "@/components/kiyana/ProductCard";

const CATS = [
  { id: "atasan", name: "Atasan", img: "https://images.pexels.com/photos/31779367/pexels-photo-31779367.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { id: "bottom", name: "Bottom", img: "https://images.unsplash.com/photo-1592595293637-8557fa6d3c64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHx3b21lbiUyMGNhc3VhbCUyMHdoaXRlJTIwdG9wJTIwb3V0Zml0JTIwc3R1ZGlvfGVufDB8fHx8MTc4OTcxMTA2NXww&ixlib=rb-4.1.0&q=85" },
  { id: "dress", name: "Dress", img: "https://images.unsplash.com/photo-1552874869-5c39ec9288dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxoaWphYiUyMGZhc2hpb24lMjBkcmVzc3xlbnwwfHx8fDE3ODk3MDUxNTB8MA&ixlib=rb-4.1.0&q=85" },
  { id: "setelan", name: "Setelan", img: "https://images.unsplash.com/photo-1651828855150-ba40f6870a53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxoaWphYiUyMGZhc2hpb24lMjBkcmVzc3xlbnwwfHx8fDE3ODk3MDUxNTB8MA&ixlib=rb-4.1.0&q=85" },
  { id: "aksesori", name: "Aksesoris", img: "https://images.pexels.com/photos/11030283/pexels-photo-11030283.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

const TABS = [
  { id: "new", name: "Terbaru" },
  { id: "bestseller", name: "Terlaris" },
  { id: "price", name: "Harga" },
];

export default function StoreCategories() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [priceAsc, setPriceAsc] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fMin, setFMin] = useState("");
  const [fMax, setFMax] = useState("");
  const [fColor, setFColor] = useState([]);
  const [fSize, setFSize] = useState([]);
  const cat = sp.get("cat") || "all";
  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "new";

  useEffect(() => {
    api.get("/products", { params: { category: cat, q } }).then((r) => setProducts(r.data));
  }, [cat, q]);

  const priceOf = (p) => p.promo_price ?? p.price;
  const sorted = [...products].sort((a, b) => {
    if (sort === "bestseller") return (b.sold_total ?? b.sold ?? 0) - (a.sold_total ?? a.sold ?? 0);
    if (sort === "price") return priceAsc ? priceOf(a) - priceOf(b) : priceOf(b) - priceOf(a);
    return 0;
  });

  const filteredSorted = sorted.filter((p) => {
    if (fMin && priceOf(p) < +fMin) return false;
    if (fMax && priceOf(p) > +fMax) return false;
    if (fColor.length && !(p.variants || []).some((v) => fColor.includes(v.color))) return false;
    if (fSize.length && !(p.variants || []).some((v) => fSize.includes(v.size))) return false;
    return true;
  });

  const toggleArr = (arr, v, setter) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="flex items-center justify-between">
        <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold">Kategori</h1>
        <button className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center">
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Circle avatars */}
      <div className="flex gap-4 overflow-x-auto no-scrollbar -mx-4 px-4">
        {CATS.map((c) => (
          <button key={c.id} data-testid={`cat-avatar-${c.id}`} onClick={() => setSp({ cat: c.id, sort })} className="flex flex-col items-center gap-1.5 flex-shrink-0">
            <div className={`w-14 h-14 rounded-full overflow-hidden border-2 ${cat === c.id ? "border-brand" : "border-blush-soft"}`}>
              <img src={c.img} alt={c.name} className="w-full h-full object-cover" />
            </div>
            <div className={`text-[10px] ${cat === c.id ? "text-brand font-semibold" : "text-cocoa"}`}>{c.name}</div>
          </button>
        ))}
      </div>

      {/* Sort tabs */}
      <div className="flex items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`sort-${t.id}`}
            onClick={() => {
              if (t.id === "price" && sort === "price") setPriceAsc(!priceAsc);
              setSp({ cat, sort: t.id });
            }}
            className={`px-4 h-8 rounded-full text-xs font-medium border flex items-center gap-1 ${
              sort === t.id ? "bg-blush text-brand border-brand" : "bg-white text-cocoa border-blush-soft"
            }`}
          >
            {t.name}
            {t.id === "price" && <ArrowUpDown className="w-3 h-3" />}
          </button>
        ))}
        <button data-testid="filter-open" onClick={() => setFilterOpen(true)} className="ml-auto px-4 h-8 rounded-full text-xs font-medium bg-white border border-blush-soft flex items-center gap-1">
          <SlidersHorizontal className="w-3 h-3" /> Filter
          {(fColor.length + fSize.length > 0 || fMin || fMax) && <span className="w-2 h-2 bg-brand rounded-full" />}
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 gap-3">
        {filteredSorted.map((p) => <ProductCard key={p.id} p={p} testid={`cat-product-${p.id}`} />)}
      </div>

      {filteredSorted.length === 0 && (
        <div className="text-center py-16 text-cocoa-muted text-sm">Belum ada produk pada filter ini.</div>
      )}

      {/* Filter sheet */}
      {filterOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setFilterOpen(false)}>
          <div className="bg-white w-full max-w-2xl mx-auto rounded-t-3xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold text-center">Filter Produk</div>
            <div>
              <div className="text-xs font-medium mb-2">Rentang Harga</div>
              <div className="flex gap-2 items-center">
                <input data-testid="filter-min" type="number" value={fMin} onChange={(e) => setFMin(e.target.value)} placeholder="Min" className="w-full h-10 px-3 rounded-xl border border-blush-soft text-sm" />
                <span className="text-cocoa-muted">—</span>
                <input data-testid="filter-max" type="number" value={fMax} onChange={(e) => setFMax(e.target.value)} placeholder="Max" className="w-full h-10 px-3 rounded-xl border border-blush-soft text-sm" />
              </div>
            </div>
            <div>
              <div className="text-xs font-medium mb-2">Warna</div>
              <div className="flex gap-2 flex-wrap">
                {["Maroon", "Rose", "White", "Navy"].map((c) => (
                  <button key={c} data-testid={`filter-color-${c}`} onClick={() => toggleArr(fColor, c, setFColor)}
                    className={`px-3.5 h-8 rounded-full text-xs border ${fColor.includes(c) ? "bg-brand text-white border-brand" : "border-blush-soft"}`}>{c}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium mb-2">Ukuran</div>
              <div className="flex gap-2">
                {["S", "M", "L", "XL"].map((s) => (
                  <button key={s} data-testid={`filter-size-${s}`} onClick={() => toggleArr(fSize, s, setFSize)}
                    className={`w-11 h-9 rounded-full text-xs border font-medium ${fSize.includes(s) ? "bg-brand text-white border-brand" : "border-blush-soft"}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button data-testid="filter-reset" onClick={() => { setFMin(""); setFMax(""); setFColor([]); setFSize([]); }} className="flex-1 h-11 rounded-full border border-blush-soft text-sm">Reset</button>
              <button data-testid="filter-apply" onClick={() => setFilterOpen(false)} className="flex-1 h-11 rounded-full bg-brand text-white text-sm font-semibold">Terapkan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
